import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Reusable grouped hover-menu.
 * groups = [{ id, label, description?, items: [{ to, label, description?, testid, icon?, accent? }] }]
 */
export default function AppHeaderMenu({ groups, activePath, onNavigate }) {
  const [openGroup, setOpenGroup] = useState(null);
  const timer = useRef(null);
  const cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const scheduleClose = () => { cancel(); timer.current = setTimeout(() => setOpenGroup(null), 120); };
  const openNow = (id) => { cancel(); setOpenGroup(id); };

  const activeGroupId = groups.find((g) =>
    (g.items || []).some((it) => activePath === it.to || (it.to !== "/" && activePath.startsWith(it.to)))
  )?.id;

  return (
    <nav
      className="flex items-stretch gap-1 h-14 w-max min-w-full"
      data-testid="app-header-menu"
    >
      {groups.map((group) => {
        // Flat link group — no dropdown
        if (group.direct && group.to) {
          const isActive = activePath === group.to || activePath.startsWith(group.to);
          return (
            <Link
              key={group.id}
              to={group.to}
              data-testid={`app-menu-group-${group.id}`}
              className={cn(
                "h-full px-4 text-xs font-semibold uppercase tracking-wider transition-colors border-b-2 flex items-center",
                isActive
                  ? "text-primary border-primary"
                  : "text-muted-foreground border-transparent hover:text-primary hover:border-accent/40"
              )}
            >
              {group.label}
            </Link>
          );
        }
        const isActive = activeGroupId === group.id;
        const isOpen = openGroup === group.id;
        return (
          <div key={group.id} className="relative">
            <button
              type="button"
              data-testid={`app-menu-group-${group.id}`}
              onMouseEnter={() => openNow(group.id)}
              onMouseLeave={scheduleClose}
              onFocus={() => openNow(group.id)}
              onClick={() => (isOpen ? setOpenGroup(null) : openNow(group.id))}
              className={cn(
                "h-full px-4 text-xs font-semibold uppercase tracking-wider transition-colors border-b-2 flex items-center gap-1",
                isActive
                  ? "text-primary border-primary"
                  : isOpen
                    ? "text-primary border-accent/60 bg-muted/40"
                    : "text-muted-foreground border-transparent hover:text-primary hover:border-accent/40"
              )}
            >
              {group.label}
              <ChevronDown size={12} className={cn("transition-transform", isOpen && "rotate-180")} />
            </button>
            {isOpen && (
              <div
                className="absolute left-0 top-full z-40 min-w-[280px] rounded-b-lg border border-t-0 border-border bg-white shadow-lg overflow-hidden"
                data-testid={`app-menu-dropdown-${group.id}`}
                onMouseEnter={() => openNow(group.id)}
                onMouseLeave={scheduleClose}
              >
                {group.description && (
                  <div className="px-4 py-2 bg-muted/40 border-b border-border">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{group.description}</div>
                    <div className="text-xs text-primary font-medium">{group.label}</div>
                  </div>
                )}
                <ul className="py-1">
                  {group.items.map((it) => {
                    const isSelected = activePath === it.to || (it.to !== "/" && activePath.startsWith(it.to));
                    const Icon = it.icon;
                    return (
                      <li key={it.to}>
                        <Link
                          to={it.to}
                          data-testid={it.testid}
                          onClick={() => { setOpenGroup(null); cancel(); onNavigate?.(); }}
                          className={cn(
                            "w-full text-left px-4 py-2 flex items-start gap-2.5 transition-colors",
                            isSelected
                              ? "bg-pastel-mint-bg text-pastel-mint-fg"
                              : "hover:bg-muted/60"
                          )}
                        >
                          {Icon && <Icon size={14} className={cn("mt-0.5 shrink-0", isSelected ? "text-pastel-mint-fg" : it.accent ? "text-accent" : "text-muted-foreground")} />}
                          <div className="min-w-0 flex-1">
                            <div className={cn("text-xs font-medium flex items-center gap-1.5", it.accent && !isSelected && "text-accent")}>
                              {it.label}
                              {isSelected && <span className="ml-auto text-[10px] uppercase tracking-wider">Active</span>}
                            </div>
                            {it.description && (
                              <div className="text-[10px] text-muted-foreground mt-0.5">{it.description}</div>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
